import { useState, useEffect } from 'react';
import moment from 'moment';
import {
	CheckCircleIcon,
	XCircleIcon,
	ClockIcon,
	ForwardIcon,
} from '@heroicons/react/24/solid';
import { ArrowPathIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { StackableModal } from '../components/nextgen';
import { AvatarGenerated } from '../components/Avatar';
import { getUserFullName, classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import { getRecurrenceRuleTasks } from '../api/tasks';

const MODAL_NAME = 'recurrenceRuleHistory';

// Recurrence labels
const RECURRENCE_LABELS = {
	daily: 'Daily',
	weekly: 'Weekly',
	biweekly: 'Biweekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	biyearly: 'Bi-yearly',
	yearly: 'Yearly',
};

const RecurrenceRuleHistoryModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openTaskDetail } = useModal('taskDetail');

	const rule = open?.rule;
	const team = open?.team || [];
	const sops = open?.sops || [];
	const onTaskUpdate = open?.onTaskUpdate;

	const [tasks, setTasks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const pageSize = 10;

	// Fetch tasks when modal opens
	useEffect(() => {
		if (open && rule?.id) {
			fetchTasks();
		}
	}, [open, rule?.id, page]);

	const fetchTasks = async () => {
		setLoading(true);
		try {
			const result = await getRecurrenceRuleTasks(rule.id, { page, pageSize });
			setTasks(result.rows || []);
			setTotalCount(result.count || 0);
		} catch (err) {
			console.error('Error fetching rule tasks:', err);
		}
		setLoading(false);
	};

	if (!rule) return null;

	const totalPages = Math.ceil(totalCount / pageSize);

	const getTaskStatus = (task) => {
		if (task.skipped) return 'skipped';
		if (task.completed) return 'completed';
		return 'pending';
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case 'completed':
				return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
			case 'skipped':
				return <ForwardIcon className="h-5 w-5 text-amber-500" />;
			default:
				return <ClockIcon className="h-5 w-5 text-gray-400" />;
		}
	};

	const getStatusLabel = (status) => {
		switch (status) {
			case 'completed':
				return (
					<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
						Completed
					</span>
				);
			case 'skipped':
				return (
					<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
						Skipped
					</span>
				);
			default:
				return (
					<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
						Pending
					</span>
				);
		}
	};

	const handleTaskClick = (task) => {
		openTaskDetail({
			task,
			team,
			sops,
			onUpdate: onTaskUpdate
				? async (taskId, updates) => {
						await onTaskUpdate(taskId, updates);
						fetchTasks(); // Refresh the list
					}
				: null,
			onToggle: onTaskUpdate
				? async (taskId, completed) => {
						await onTaskUpdate(taskId, { completed });
						fetchTasks(); // Refresh the list
					}
				: null,
		});
	};

	return (
		<StackableModal open={!!open} onClose={() => setClose()} size="lg">
			<div className="space-y-4">
				{/* Header */}
				<div>
					<h2 className="text-lg font-semibold text-gray-900">Task History</h2>
					<p className="text-sm text-gray-500 mt-1">{rule.content}</p>
					<div className="flex items-center gap-3 mt-2">
						<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
							<ArrowPathIcon className="h-3 w-3" />
							{RECURRENCE_LABELS[rule.recurrence]}
						</span>
						{rule.nextOccurrence && rule.active && (
							<span className="text-xs text-gray-500 flex items-center gap-1">
								<CalendarIcon className="h-3 w-3" />
								Next: {moment.utc(rule.nextOccurrence).format('MMM D, YYYY')}
							</span>
						)}
						{!rule.active && (
							<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
								Paused
							</span>
						)}
					</div>
				</div>

				{/* Tasks List */}
				<div className="border-t border-gray-100 pt-4">
					{loading ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
						</div>
					) : tasks.length === 0 ? (
						<div className="text-center py-8">
							<ClockIcon className="mx-auto h-8 w-8 text-gray-300" />
							<p className="mt-2 text-sm text-gray-500">No tasks generated yet</p>
						</div>
					) : (
						<div className="space-y-2">
							{tasks.map((task) => {
								const status = getTaskStatus(task);
								const dueDate = task.dueDate || task.due_date;
								return (
									<div
										key={task.id}
										onClick={() => handleTaskClick(task)}
										className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
									>
										{/* Status Icon */}
										<div className="flex-shrink-0 mt-0.5">
											{getStatusIcon(status)}
										</div>

										{/* Content */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2 flex-wrap">
												{getStatusLabel(status)}
												{dueDate && (
													<span className="text-xs text-gray-500">
														Due: {moment.utc(dueDate).format('MMM D, YYYY')}
													</span>
												)}
											</div>
											<div className="mt-1.5 text-xs text-gray-500">
												{status === 'completed' && task.completedAt && (
													<span>
														Completed{' '}
														{moment(task.completedAt).format(
															'MMM D, YYYY [at] h:mmA',
														)}
														{task.completedByUser && (
															<span>
																{' '}
																by {getUserFullName(task.completedByUser)}
															</span>
														)}
													</span>
												)}
												{status === 'skipped' && task.skippedAt && (
													<span>
														Skipped{' '}
														{moment(task.skippedAt).format(
															'MMM D, YYYY [at] h:mmA',
														)}
													</span>
												)}
												{status === 'pending' && (
													<span>
														Created{' '}
														{moment(task.createdAt).format(
															'MMM D, YYYY [at] h:mmA',
														)}
													</span>
												)}
											</div>
										</div>

										{/* Assigned User */}
										{task.assignedToUser && (
											<div className="flex-shrink-0">
												<AvatarGenerated
													id={getUserFullName(task.assignedToUser)}
													size="xs"
													title={getUserFullName(task.assignedToUser)}
												/>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="flex items-center justify-between pt-4 border-t border-gray-100">
						<p className="text-sm text-gray-500">
							Showing {(page - 1) * pageSize + 1} -{' '}
							{Math.min(page * pageSize, totalCount)} of {totalCount} tasks
						</p>
						<div className="flex gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
								className={classNames(
									'px-3 py-1.5 text-sm font-medium rounded-md',
									page === 1
										? 'text-gray-400 bg-gray-100 cursor-not-allowed'
										: 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50',
								)}
							>
								Previous
							</button>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page === totalPages}
								className={classNames(
									'px-3 py-1.5 text-sm font-medium rounded-md',
									page === totalPages
										? 'text-gray-400 bg-gray-100 cursor-not-allowed'
										: 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50',
								)}
							>
								Next
							</button>
						</div>
					</div>
				)}

				{/* Actions */}
				<div className="flex justify-end pt-4 border-t border-gray-100">
					<button
						type="button"
						onClick={() => setClose()}
						className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
					>
						Close
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default RecurrenceRuleHistoryModal;
